import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { AxiosResponse } from 'axios'

import { axiosInstance } from '../../helpers'
import {
    GuestCardCustomQuestion,
    GuestCardQuestionType,
} from '../../models/Outreach/types'

export interface CreateCustomQuestionRequest {
    question: string
    question_type: GuestCardQuestionType
    options?: string[]
}

export interface UpdateCustomQuestionRequest {
    id: number
    question?: string
    question_type?: GuestCardQuestionType
    options?: string[]
}

export const useCustomQuestions = () => {
    const queryClient = useQueryClient()

    const {
        data: customQuestions,
        isLoading: isLoadingCustomQuestions,
        error: errorCustomQuestions,
    } = useQuery<AxiosResponse<GuestCardCustomQuestion[]>, Error>({
        queryKey: ['custom-questions'],
        queryFn: () => axiosInstance.get('/outreach/custom-questions/'),
        enabled: true,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const createCustomQuestion = useMutation({
        mutationFn: (request: CreateCustomQuestionRequest) =>
            axiosInstance.post('/outreach/custom-questions/', request),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['custom-questions'],
            })
            toast.success('Question created successfully')
        },
        onError: () => {
            toast.error('Failed to create question')
        },
    })

    const updateCustomQuestion = useMutation({
        mutationFn: (request: UpdateCustomQuestionRequest) =>
            axiosInstance.patch(
                `/outreach/custom-questions/${request.id}/`,
                request,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['custom-questions'],
            })
            toast.success('Question updated successfully')
        },
        onError: () => {
            toast.error('Failed to update question')
        },
    })

    const deleteCustomQuestion = useMutation({
        mutationFn: (id: number) =>
            axiosInstance.delete(`/outreach/custom-questions/${id}/`),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['custom-questions'],
            })
            toast.success('Question archived successfully')
        },
        onError: () => {
            toast.error('Failed to archive question')
        },
    })

    return {
        customQuestions: customQuestions?.data,
        isLoadingCustomQuestions,
        errorCustomQuestions,
        createCustomQuestion,
        updateCustomQuestion,
        deleteCustomQuestion,
    }
}
